import { KeyboardArrowDown } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Paper,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { reducerUpdateAdvanceSearch } from 'src/features/advanceSearchEcommerce';
import { TSentiment } from 'src/models/general';

const sentimentOptions: TSentiment[] = ['positive', 'neutral', 'negative'];

export default function FilterSentiment() {
  const theme = useTheme();
  const { sentiment } = useAppSelector(
    (state) => state.storeAdvancedSearchEcommerce
  );
  const [open, setOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const dispatch = useAppDispatch();

  const advanceSearchEcommerce = useAppSelector(
    (state) => state.storeAdvancedSearchEcommerce
  );

  const [selectedSentimentLocal, setSelectedSentimentLocal] = useState<
    TSentiment[]
  >([]);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };

  const handleSentimentCheckChange = (
    sentiment: TSentiment,
    checked: boolean
  ) => {
    if (checked) {
      setSelectedSentimentLocal([...selectedSentimentLocal, sentiment]);
    } else {
      setSelectedSentimentLocal(
        selectedSentimentLocal.filter((item) => item !== sentiment)
      );
    }
  };

  const handleApply = () => {
    dispatch(
      reducerUpdateAdvanceSearch({
        ...advanceSearchEcommerce,
        sentiment: selectedSentimentLocal
      })
    );
    handleClose();
  };

  useEffect(() => {
    setSelectedSentimentLocal(sentiment);
  }, [sentiment]);

  return (
    <>
      <Stack maxWidth="400px" width="100%" spacing={0.5}>
        <Typography variant="h5" fontWeight={600}>
          Sentiment
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={handleOpen}
          sx={{
            color: 'text.primary',
            backgroundColor: 'white',
            borderRadius: theme.spacing(1),
            minHeight: '36px',
            borderColor: (theme) =>
              open ? theme.palette.primary.main : 'rgba(0, 0, 0, 0.23)',
            '&:hover': {
              borderColor: 'rgba(0, 0, 0, 0.87)',
              backgroundColor: 'white'
            },
            '& .MuiButton-startIcon': { marginRight: 'auto' },
            '& .buttonText': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
              textAlign: 'left',
              flex: '1'
            },
            padding: '4px 8px'
          }}
          endIcon={
            <KeyboardArrowDown
              sx={{
                transform: open ? 'rotate(180deg)' : 'rotate(0)',
                marginRight: '2px'
              }}
            />
          }
        >
          {sentiment.length <= 0 ? (
            <span className="buttonText">All Sentiment</span>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'nowrap',
                gap: 0.5,
                width: '100%',
                overflow: 'scroll',
                scrollbarWidth: 'none'
              }}
            >
              {sentiment.map((label, index) => (
                <Chip
                  key={`${label}-${index}`}
                  variant="outlined"
                  sx={{
                    maxWidth: sentiment.length > 1 ? '150px' : '300px',
                    '& .MuiChip-label': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'block'
                    }
                  }}
                  label={label}
                  size="small"
                />
              ))}
            </Box>
          )}
        </Button>
      </Stack>
      <Popover
        open={open}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      >
        <Box sx={{ p: theme.spacing(2), maxWidth: '600px', width: '100%' }}>
          <Box sx={{ maxHeight: '300px', overflow: 'auto' }}>
            <>
              {sentimentOptions.map((row) => (
                <Stack direction="row" gap={0.5} key={row} alignItems="center">
                  <Checkbox
                    color="primary"
                    checked={selectedSentimentLocal.some(
                      (item) => item === row
                    )}
                    onChange={(event) =>
                      handleSentimentCheckChange(row, event.target.checked)
                    }
                  />
                  <Typography>
                    {row.charAt(0).toUpperCase() + row.slice(1)}
                  </Typography>
                </Stack>
              ))}
            </>
          </Box>
          <Stack pt={2} alignItems="center">
            <Button variant="contained" onClick={handleApply}>
              Apply
            </Button>
          </Stack>
        </Box>
      </Popover>
    </>
  );
}
